/**
 * Chat everywhere (desktop, ≥ 900 px): a docked panel that slides in from the right on any page —
 * including the design editor, where "แชทของงานนี้" opens pre-selected and the editor narrows
 * instead of being covered (body[data-chat-dock] → --xc-dock-w). Ctrl+Shift+M toggles it.
 * Closed, up to three conversations stay as round "mini chat" bubbles (like LINE / Facebook on PC).
 */
import { Suspense, useCallback, useEffect, useState } from 'react';
import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left.mjs'; import ExternalLink from 'lucide-react/dist/esm/icons/external-link.mjs'; import MessageCircle from 'lucide-react/dist/esm/icons/message-circle.mjs'; import X from 'lucide-react/dist/esm/icons/x.mjs';
import { describeError } from '../api/client.js';
import { SkeletonRows } from '../components/PageSkeleton.js';
import { useToast } from '../components/Toast.js';
import { lazyPage } from '../lib/lazy.js';
import { useMediaQuery } from '../lib/useMediaQuery.js';
import { useLocation } from '../lib/router.js';
import { chatApi } from './api.js';
import { ConvAvatar } from './Avatar.js';
import { popOut } from './popout.js';
import { chatStore, useChat } from './store.js';
import { onDockRequest, useWorkContext } from './workContext.js';

// The conversation view (composer, message cards, emoji…) is downloaded when the dock first opens.
const DockPanel = lazyPage(() => import('./DockPanel.js'), 'DockPanel');
/** Warm the dock's chunk (the header button / a mini bubble under the pointer). */
export const preloadDock = () => DockPanel.preload();

const MINI_KEY = 'xc.chat.mini';
const readMini = (): string[] => {
  try {
    return (JSON.parse(sessionStorage.getItem(MINI_KEY) ?? '[]') as string[]).slice(0, 3);
  } catch {
    return [];
  }
};

let toggleDock: ((open?: boolean) => void) | null = null;
/** Ctrl+Shift+M and the header button. */
export function setDockOpen(open?: boolean) {
  toggleDock?.(open);
}

export function ChatDock() {
  const store = useChat();
  const notify = useToast();
  const loc = useLocation();
  const work = useWorkContext();
  const desktop = useMediaQuery('(min-width: 900px)');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState<string | null>(null);
  const [mini, setMini] = useState<string[]>(readMini);
  const onChatPage = loc.path === '/chat' || loc.path.startsWith('/chat/');

  const remember = (ids: string[]) => {
    setMini(ids);
    try {
      sessionStorage.setItem(MINI_KEY, JSON.stringify(ids));
    } catch {
      /* private mode */
    }
  };

  const show = useCallback((id: string | null) => {
    setActive(id);
    setOpen(true);
    if (id) remember([id, ...readMini().filter((x) => x !== id)].slice(0, 3));
  }, []);

  useEffect(() => {
    toggleDock = (v) => setOpen((o) => (v === undefined ? !o : v));
    return () => {
      toggleDock = null;
    };
  }, []);

  // "แชทของงานนี้" / a design-chat link: open the dock on that conversation.
  useEffect(() => onDockRequest(async (r) => {
    try {
      if (r.conversationId) return show(r.conversationId);
      if (r.designId) {
        const c = await chatApi.designChat(r.designId);
        chatStore.upsertConversation(c);
        show(c.id);
      } else setOpen(true);
    } catch (e) {
      notify(describeError(e), 'error');
    }
  }), [show, notify]);

  // The editor narrows while the dock is open (wide screens), instead of being covered.
  useEffect(() => {
    const on = open && desktop && !onChatPage;
    if (on) document.body.dataset.chatDock = 'open';
    else delete document.body.dataset.chatDock;
    return () => {
      delete document.body.dataset.chatDock;
    };
  }, [open, desktop, onChatPage]);

  // Esc closes the dock when focus is inside it (and no dialog is on top).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && (e.target as HTMLElement | null)?.closest?.('.xc-dock') && !document.querySelector('[role="dialog"][aria-modal="true"]')) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!desktop || onChatPage || !store.me) return null;
  const conv = active ? store.conversations.get(active) ?? null : null;
  if (active && !conv && store.listLoaded) void store.refreshConversation(active);
  const bubbles = mini.map((id) => store.conversations.get(id)).filter((c): c is NonNullable<typeof c> => !!c);
  const designChat = work ? store.sorted().find((c) => c.kind === 'design' && c.designId === work.designId) : undefined;

  // Full-screen work (editor, presenting): no floating bubbles — the header button / Ctrl+Shift+M open the dock.
  // `/photo/<asset>` is the Photo Studio's full-screen editor (ADR 0031); `/photo` itself is an ordinary page.
  const fullScreen = loc.path.startsWith('/d/') || loc.path.startsWith('/present/') || loc.path.startsWith('/s/') || loc.path === '/demo' || loc.path.startsWith('/office/') || (loc.path.startsWith('/photo/') && loc.path !== '/photo/collage');
  if (!open && fullScreen) return null;
  if (!open) {
    return (
      <div className="xc-mini-chats" aria-label="แชทที่เปิดไว้" onPointerEnter={preloadDock} onFocus={preloadDock}>
        {bubbles.map((c) => (
          <button key={c.id} type="button" className="xc-mini-bubble" onClick={() => show(c.id)} aria-label={`เปิดแชท ${c.title}${c.unread ? ` ยังไม่อ่าน ${c.unread}` : ''}`} title={c.title}>
            <ConvAvatar c={c} me={store.me} size={48} />
            {c.unread > 0 && <span className="xc-count-badge">{c.unread > 99 ? '99+' : c.unread}</span>}
          </button>
        ))}
        <button type="button" className="xc-mini-bubble is-main" onClick={() => setOpen(true)} aria-label={`เปิดแถบแชท (Ctrl+Shift+M)${store.unreadChats ? ` ยังไม่อ่าน ${store.unreadChats} แชท` : ''}`} title="แชท (Ctrl+Shift+M)">
          <MessageCircle size={24} aria-hidden="true" />
          {store.unreadChats > 0 && <span className="xc-count-badge">{store.unreadChats}</span>}
        </button>
      </div>
    );
  }

  return (
    <aside className="xc-dock" aria-label="แถบแชท">
      <div className="xc-dock-head">
        {conv ? (
          <button type="button" className="xc-icon-btn" aria-label="กลับไปรายการแชท" onClick={() => setActive(null)}><ArrowLeft size={18} aria-hidden="true" /></button>
        ) : <MessageCircle size={18} aria-hidden="true" />}
        <strong>{conv ? conv.title : 'แชท'}</strong>
        {work && !conv && (
          <button type="button" className="xc-btn xc-btn-sm" onClick={() => (designChat ? show(designChat.id) : void chatApi.designChat(work.designId).then((c) => { chatStore.upsertConversation(c); show(c.id); }).catch((e) => notify(describeError(e), 'error')))}>
            แชทของงานนี้
          </button>
        )}
        {conv && <button type="button" className="xc-icon-btn" aria-label="เปิดในหน้าต่างใหม่" onClick={() => popOut(conv.id)}><ExternalLink size={16} aria-hidden="true" /></button>}
        {conv && <button type="button" className="xc-icon-btn" aria-label="ย่อเป็นฟองแชท" onClick={() => { setOpen(false); }}>–</button>}
        <button type="button" className="xc-icon-btn" aria-label="ปิดแถบแชท" onClick={() => { setOpen(false); if (conv) remember(mini.filter((x) => x !== conv.id)); }}><X size={18} aria-hidden="true" /></button>
      </div>
      <div className="xc-dock-body">
        <Suspense fallback={<SkeletonRows count={7} label="กำลังโหลดแชท…" />}>
          <DockPanel conv={conv} onOpen={show} />
        </Suspense>
      </div>
    </aside>
  );
}
